import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import { RefreshCw, CheckCircle, Smartphone, Zap, RotateCcw } from 'lucide-react';
import { toast } from 'react-toastify';
import ParticleService from '../ParticleService';


// Helper for User Passwords
function randomPSD() {
    const adj = ["Happy", "Bright", "Cool", "Swift", "Silent"];
    const noun = ["Badger", "Wombat", "Eagle", "Bear", "Fox"];
    const num = Math.floor(Math.random() * 90 + 10);
    return `${adj[Math.floor(Math.random() * adj.length)]}${noun[Math.floor(Math.random() * noun.length)]}${num}`;
}

// Helper for Device Names
function generateDeviceName() {
    const adj = ["Silent", "Super", "Mega", "Hyper", "Rapid"];
    const animal = ["Badger", "Wombat", "Falcon", "Hawk", "Eagle"];
    const num = Math.floor(Math.random() * 99) + 10;
    return `${adj[Math.floor(Math.random() * adj.length)]}${animal[Math.floor(Math.random() * animal.length)]}${num}`;
}

export default function UserManagementModal({ show, onHide }) {
    // User Form State
    const [newUser, setNewUser] = useState('');
    const [newUserName, setNewUserName] = useState('');
    const [newUserPsd, setNewUserPsd] = useState(randomPSD());
    const [isLoading, setIsLoading] = useState(false);
    
    // Success State
    const [createdToken, setCreatedToken] = useState('');
    const [welcomeEmailSent, setWelcomeEmailSent] = useState(false);
    const [welcomeEmailWarning, setWelcomeEmailWarning] = useState('');

    // Provisioning Form State
    const [deviceId, setDeviceId] = useState('');
    const [deviceName, setDeviceName] = useState(generateDeviceName());
    const [isProvisioning, setIsProvisioning] = useState(false);

    // --- Handlers ---

    const handleGenerateUserPass = () => setNewUserPsd(randomPSD());
    const handleGenerateDeviceName = () => setDeviceName(generateDeviceName());

    const handleAddUser = async () => {
        if (!newUser || !newUserPsd) {
            toast.warn("Please fill in email and password.");
            return;
        }

        setIsLoading(true);
        try {
            const result = await ParticleService.makeUser(newUser, newUserPsd, ParticleService.LTEProductID);
            
            if (result.error) {
                toast.error(typeof result.error === 'string' ? result.error : "Error creating user");
            } else if (result.access_token) {
                setWelcomeEmailSent(Boolean(result.welcomeEmailSent));
                setWelcomeEmailWarning(result.welcomeEmailWarning || '');
                if (result.welcomeEmailSent) toast.success(`User created and welcome email sent to ${newUser}.`);
                else toast.warning(result.welcomeEmailWarning || 'User created, but the welcome email was not sent.');
                setNewUserPsd('');
                document.dispatchEvent(new Event('dataRefresh'));
                setCreatedToken(result.access_token);
            } else {
                 toast.info("Request sent, but no token returned. Check logs.");
            }
        } catch (error) {
            toast.error("Failed to connect to service.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleProvisionDevice = async () => {
        if (!deviceId) {
            toast.warn("Please enter a Device ID.");
            return;
        }

        setIsProvisioning(true);
        try {
            // 1. Claim
            const claimResult = await ParticleService.addDeviceUser(createdToken, deviceId);
            if (claimResult.error) {
                toast.error(claimResult.error);
                setIsProvisioning(false);
                return;
            }

            // 2. Rename
            const renameResult = await ParticleService.renameDevice(deviceId, deviceName, createdToken);
            if (renameResult.error) {
                toast.warning("Device added, but rename failed.");
            } else {
                toast.success(`Device ${deviceName} added successfully!`);
            }

            // 3. Reset for next device
            document.dispatchEvent(new Event('dataRefresh'));
            setDeviceId('');
            setDeviceName(generateDeviceName());

        } catch (e) {
            toast.error("Provisioning failed.");
            console.error(e);
        } finally {
            setIsProvisioning(false);
        }
    };

    const handleClose = () => {
        // Reset ALL state when closing
        setNewUser('');
        setNewUserName('');
        setNewUserPsd(randomPSD());
        setCreatedToken('');
        setWelcomeEmailSent(false);
        setWelcomeEmailWarning('');
        setDeviceId('');
        setDeviceName(generateDeviceName());
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered backdrop="static" size={createdToken ? 'lg' : 'md'}>
            <Modal.Header closeButton>
                <Modal.Title>{createdToken ? 'Particle Account Created - Add Devices' : 'Add Particle Account'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {createdToken ? (
                    // --- SUCCESS & PROVISIONING VIEW ---
                    <div>
                        <div className="text-center mb-3">
                            <CheckCircle className="text-success mb-2" size={40} />
                            <h5 className="text-success">Particle Account Created!</h5>
                            <p className="small text-muted mb-0"><strong>{newUser}</strong> is ready.</p>
                        </div>
                        <Alert variant={welcomeEmailSent ? 'success' : 'warning'}>
                            {welcomeEmailSent
                                ? `The welcome email and temporary password were sent automatically to ${newUser}.`
                                : welcomeEmailWarning || 'The welcome email could not be sent. Use the password-free resend tool in Production.'}
                        </Alert>
                        <div className="p-2">
                            <h6 className="fw-bold mb-3 d-flex align-items-center">
                                <Smartphone className="me-2 text-primary" size={18} />
                                Quick Add Device
                            </h6>
                            <Form>
                                <FloatingLabel controlId="floatDevId" label="Device ID (Hex)" className="mb-2">
                                    <Form.Control 
                                        type="text" 
                                        placeholder="Device ID"
                                        value={deviceId}
                                        onChange={(e) => setDeviceId(e.target.value)}
                                    />
                                </FloatingLabel>

                                <div className="d-flex mb-3">
                                    <FloatingLabel controlId="floatDevName" label="Device Name" className="flex-grow-1">
                                        <Form.Control 
                                            type="text" 
                                            placeholder="Name"
                                            value={deviceName}
                                            onChange={(e) => setDeviceName(e.target.value)}
                                        />
                                    </FloatingLabel>
                                    <Button variant="outline-secondary" className="ms-2" onClick={handleGenerateDeviceName} title="Generate Name">
                                        <RotateCcw size={18} />
                                    </Button>
                                </div>

                                <div className="d-grid">
                                    <Button variant="primary" onClick={handleProvisionDevice} disabled={isProvisioning}>
                                        {isProvisioning ? (
                                            <span><Zap className="me-1" size={16} /> Adding...</span>
                                        ) : (
                                            <span>Add Device to User</span>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </div>
                    </div>
                ) : (
                    // --- CREATE USER FORM VIEW (Unchanged) ---
                    <Form>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingInputEmail" label="Email Address">
                                    <Form.Control 
                                        type="email" 
                                        placeholder="name@example.com"
                                        value={newUser}
                                        onChange={e => setNewUser(e.target.value)} 
                                    />
                                </FloatingLabel>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingInputName" label="Display Name (Optional)">
                                    <Form.Control 
                                        type="text" 
                                        placeholder="John Doe"
                                        value={newUserName}
                                        onChange={e => setNewUserName(e.target.value)} 
                                    />
                                </FloatingLabel>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col xs={8}>
                                <FloatingLabel controlId="floatingInputPsd" label="Password">
                                    <Form.Control 
                                        type="text" 
                                        placeholder="Password"
                                        value={newUserPsd}
                                        onChange={e => setNewUserPsd(e.target.value)} 
                                    />
                                </FloatingLabel>
                            </Col>
                            <Col xs={4} className="d-flex align-items-center">
                                 <Button variant="outline-info" onClick={handleGenerateUserPass} className="w-100 h-75 d-flex align-items-center justify-content-center">
                                    <RefreshCw className="me-2" size={18} /> Gen
                                 </Button>
                            </Col>
                        </Row>
                        <Form.Text className="text-muted">
                            This temporary password is emailed automatically when the account is created and is not stored by Cannon Control.
                        </Form.Text>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                {createdToken ? (
                    <Button variant="success" onClick={handleClose}>
                        Done / Close
                    </Button>
                ) : (
                    <>
                        <Button variant="secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleAddUser} disabled={isLoading}>
                            {isLoading ? 'Creating...' : 'Create Particle Account'}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}
